/**
 * LavadoForm — formulario de Crear/Editar lavado individual.
 *
 * Round 2026-05-06: rediseño UX por pedido del usuario:
 *  1. Fecha por defecto = hoy (manejado en handleNuevo del hook padre).
 *  2. Cantidades y precios unitarios → OPCIONALES.
 *  3. Total (neto / con IVA) sincronizados bidireccionalmente — campo
 *     OBLIGATORIO. Si el usuario llena cantidades/precios, suma y
 *     completa el total. Si llena el total directo, los detalles
 *     quedan vacíos (el monto se persiste tal cual).
 *  4. Estructura visual con secciones (General / Detalle / Extras /
 *     Total / Pagos) usando dividers.
 *
 * Round 2026-04-27 (Fase 8) original: extraído de lavanderia.tsx para
 * sacar ~350 LOC del archivo padre y aislar la lógica del split de
 * "Pagado por" / "Pagado a" según rol de Aloja Fácil.
 *
 * REGLAS DE NEGOCIO RELEVANTES (ver docs/modulo-lavanderia.md):
 *  - "Pagado por" y "Pagado a" no pueden coincidir cuando ambos son rol
 *    simple (administrador/propietario/arrendatario).
 *  - "propietario" como rol solo se ofrece si hay propiedad seleccionada.
 *  - "lavanderia_catalogo" como rol pre-llena la lavandería del filtro activo
 *    como default cómodo, pero queda editable (no se bloquea).
 *  - IVA centralizado en `utils/iva.ts` (round 2026-05-06).
 */

import React from "react";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
import { getMsg } from "../../../utils/antdStatic";
import { getIvaFactor, conIvaFromNeto, netoFromConIva } from "../../../utils/iva";
import { userSelectOption } from "../../../utils/userDisplay";
import { formatPropertyName } from "../../../utils/propertyDisplay";

// ─── PrecioIvaInput ─────────────────────────────────────────────────────
//
// Sub-componente de input dual: muestra el valor en NETO y CON IVA al
// mismo tiempo, sincronizados. Internamente persiste solo el valor "con
// IVA" (lo que el form guarda); el neto es derivado para UX. El usuario
// puede editar cualquiera de los dos lados — el otro se completa solo.
//
// IVA centralizado en `utils/iva.ts` — si cambia la tasa, solo ahí.
const PrecioIvaInput: React.FC<{
  value?: number;
  onChange?: (v: number) => void;
}> = ({ value, onChange }) => {
  const conIva = value || 0;
  const neto = netoFromConIva(conIva);

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
          Neto
        </div>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          placeholder="0"
          value={neto || null}
          onChange={(v) => onChange?.(conIvaFromNeto(Number(v) || 0))}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
          Con IVA
        </div>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          placeholder="0"
          value={conIva || null}
          onChange={(v) => onChange?.(Number(v) || 0)}
        />
      </div>
    </div>
  );
};

// ─── Tipos auxiliares ───────────────────────────────────────────────────

interface PropiedadOpt {
  id: number;
  nombre: string;
}

interface UsuarioOpt {
  id: number;
  rol: string;
  nombre: string;
}

interface RolOpt {
  value: string;
  label: string;
}

interface LavanderiaOpt {
  id: number;
  nombre: string;
  activo: boolean;
  // Round 2026-05-12 iter 34: discriminador del catálogo. true → la
  // Genérica Externa (placeholder de proveedores externos). false → AF
  // interna (sub-entidad con contabilidad propia). El form agrupa
  // visualmente ambas categorías en el Select de "Lavandería".
  es_generica?: boolean;
}

interface LavadoFormProps {
  form: FormInstance;
  propiedades: PropiedadOpt[];
  usuarios: UsuarioOpt[];
  usuariosPorRol: Map<string, { id: number; nombre: string }[]>;
  crearOtro: (nombre: string) => Promise<{ id: number; nombre: string }>;
  ROLES: RolOpt[];
  lavanderias: LavanderiaOpt[];
  modo: string | number;
  // Round 2026-05-07: cuando el lavado vino de un arriendo (booking_id
  // != null), el usuario no puede editarlo desde acá pero SÍ debe poder
  // verlo. Cuando readOnly=true, el form se renderiza "view-only":
  // antd Form propaga `disabled` a todos los inputs descendientes (v5
  // feature; ver https://ant.design/components/form#api), incluyendo los
  // anidados en PrecioIvaInput. Ver lavanderia.tsx onVer.
  readOnly?: boolean;
  // Round 2026-05-12 iter 34 (antes iter 28 "simpleMode"): modo del modal
  // para "Costo real" de la Lavandería General Externa. NO oculta los
  // pagos — el costo real es un movimiento financiero real con su
  // estructura completa (rol AF + pagado_por + pagado_a). Lo que SÍ
  // ocultamos son las secciones "Detalle del lavado" (cantidades+precios)
  // y "Extras" — no aplican a un costo agregado anotado a mano. Además
  // agregamos el campo `nombre` arriba (concepto del costo) y el bloque
  // "Pagado a" → Lavandería queda bloqueado a la Genérica (ver lógica
  // en el render).
  costoRealMode?: boolean;
  // Round 2026-06-22: el lavado que se edita está VINCULADO a un arriendo
  // (booking_id != null). En ese caso "Propietario" (cliente) se deshabilita
  // como pagador: su costo ya va en la limpieza descontada al cliente, así que
  // cobrárselo sería doble cobro (el backend también lo rechaza). Solo aplica a
  // lavados aledaños. Ver flujo-financiero.md §7.6.
  esVinculado?: boolean;
}

// ─── Sub-componente: encabezado de sección ──────────────────────────────
//
// Visual leve con ícono + título — divide el form en bloques temáticos
// para que se vea bonito (pedido del usuario round 2026-05-06).
const SectionHeader: React.FC<{ icon: string; title: string; hint?: string }> = ({
  icon,
  title,
  hint,
}) => (
  <div style={{ marginBottom: 8, marginTop: 4 }}>
    <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>
      {icon} {title}
    </div>
    {hint && (
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{hint}</div>
    )}
  </div>
);

// ─── Componente principal ──────────────────────────────────────────────

const LavadoForm: React.FC<LavadoFormProps> = ({
  form,
  propiedades,
  usuarios: _usuarios,
  usuariosPorRol,
  crearOtro,
  ROLES,
  lavanderias,
  modo,
  readOnly = false,
  costoRealMode = false,
  esVinculado = false,
}) => {
  // useWatch — necesario para REACCIONAR a cambios y auto-calcular el
  // total cuando el usuario llena cantidades/precios.
  const precioLavado = Form.useWatch("precioLavado", form) || 0;
  const precioSecado = Form.useWatch("precioSecado", form) || 0;
  const cantidadLavados = Form.useWatch("cantidadLavados", form) || 0;
  const cantidadSecados = Form.useWatch("cantidadSecados", form) || 0;
  const extraMonto = Form.useWatch("extraMonto", form) || 0;
  const monto = Form.useWatch("monto", form) || 0;
  const montoNeto = netoFromConIva(monto);

  // Round 2026-07-21: las opciones de "Rol de Aloja Fácil" dependen del tipo
  // de lavandería del filtro (`modo` = id de la lavandería seleccionada):
  //   - Genérica externa (es_generica=true): 'paga' (costo real) e
  //     'intermediario' (pass-through neto 0). 'recibe' (estimado) es AUTO del
  //     booking, no se crea a mano.
  //   - AF interna (es_generica=false): 'intermediario' (cascada normal). El
  //     backend rechaza 'paga' para una AF interna (ver flujo-financiero.md §7.3).
  // Preservamos el valor actual si no está en la lista (ej. 'recibe' legacy
  // §18.3 al ver/editar) para que un registro histórico no pierda su rol.
  const rolAFActual = Form.useWatch("rolDeAlojaFacil", form);
  const lavDelModo =
    typeof modo === "number" ? lavanderias.find((l) => l.id === modo) : null;
  const esGenericaModo = lavDelModo?.es_generica === true;
  const rolOptionsBase = esGenericaModo
    ? [
        { value: "intermediario", label: "Intermediario (neto 0)" },
        { value: "paga", label: "Paga (costo real)" },
      ]
    : [{ value: "intermediario", label: "Intermediario" }];
  const rolOptions =
    rolAFActual && !rolOptionsBase.some((o) => o.value === rolAFActual)
      ? [
          ...rolOptionsBase,
          {
            value: rolAFActual,
            label: rolAFActual.charAt(0).toUpperCase() + rolAFActual.slice(1),
          },
        ]
      : rolOptionsBase;

  // Auto-cálculo del monto desde detalles. La regla nueva (round 2026-05-06):
  //   - Si la suma de detalles+extra > 0 → escribe `monto`.
  //   - Si la suma === 0 → NO toca `monto` (el usuario puede haberlo escrito
  //     directo y los detalles están en blanco — flujo válido).
  // Esto permite el "atajo" pedido por el usuario: llenar solo el total y
  // dejar lavado/secado/extra vacíos.
  React.useEffect(() => {
    const total =
      Number(cantidadLavados) * Number(precioLavado) +
      Number(cantidadSecados) * Number(precioSecado) +
      Number(extraMonto);

    if (total > 0) {
      form.setFieldsValue({ monto: total });
    }
  }, [
    precioLavado,
    precioSecado,
    cantidadLavados,
    cantidadSecados,
    extraMonto,
    form,
  ]);

  // Setter del monto total con IVA (cuando el usuario edita directamente
  // el campo Total). Persistimos en `monto` (con IVA) que es la fuente
  // de verdad del backend.
  const setMontoConIva = (v: number) => {
    form.setFieldsValue({ monto: Math.round(v) });
  };
  // Setter desde el lado neto: convertimos a con IVA.
  const setMontoFromNeto = (n: number) => {
    setMontoConIva(Math.round((Number(n) || 0) * getIvaFactor()));
  };

  return (
    // `disabled` en <Form> es feature de antd v5: propaga a TODOS los
    // inputs antd descendientes (incluso anidados en sub-componentes
    // como PrecioIvaInput). Sin readOnly el comportamiento es idéntico
    // al previo.
    <Form form={form} layout="vertical" size="middle" disabled={readOnly}>
      {/* ── 📅 General ─────────────────────────────────────────────── */}
      <SectionHeader
        icon={costoRealMode ? "💸" : "📅"}
        title={costoRealMode ? "General (costo real)" : "General"}
        hint={
          costoRealMode
            ? "Anota el monto que el proveedor externo cobró efectivamente. La fecha por defecto es hoy."
            : "La fecha por defecto es hoy. Es lo único realmente obligatorio acá."
        }
      />
      <div className="flex-container">
        <Form.Item
          name="fecha"
          label="Fecha"
          rules={[{ required: true, message: "Selecciona la fecha del lavado" }]}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </Form.Item>

        {/* Round 2026-05-12 iter 34: el campo "Nombre / concepto" solo aplica
            al sub-tab Costo real (ej. "Lavandería Tomás", "Transporte mayo").
            En los lavados operativos no tiene sentido — el "qué" se deduce
            de cantidades + propiedad. */}
        {costoRealMode && (
          <Form.Item
            name="nombre"
            label="Nombre / concepto"
            rules={[{ required: true, message: "Ingresa un nombre o concepto" }]}
            tooltip="Ej.: 'Lavandería Tomás', 'Pago a transportista X'."
          >
            <Input placeholder="Lavandería Tomás" maxLength={100} />
          </Form.Item>
        )}

        <Form.Item name="propiedadId" label="Propiedad">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Seleccionar propiedad"
            options={propiedades.map((p) => ({
              value: p.id,
              label: formatPropertyName(p.nombre),
            }))}
          />
        </Form.Item>

        <Form.Item
          className="full-width"
          name="descripcion"
          label="Descripción"
        >
          <Input.TextArea rows={2} />
        </Form.Item>
      </div>

      {/* Round 2026-05-12 iter 34: Detalle del lavado y Extras NO aplican
          al sub-tab Costo real (anotamos un movimiento agregado, no una
          operación con cantidades unitarias). Las ocultamos por
          `costoRealMode` para no confundir al admin con campos huérfanos. */}
      {!costoRealMode && (
        <>
          <Divider style={{ margin: "12px 0" }} />

          {/* ── 🧺 Detalle del lavado ─────────────────────────────────── */}
          <SectionHeader
            icon="🧺"
            title="Detalle del lavado (opcional)"
            hint="Completa cantidades y precios unitarios si los tienes; si no, salta directo a Total."
          />
          <div className="flex-container">
            <Form.Item name="cantidadLavados" label="Cant. Lavados">
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>
            <Form.Item name="cantidadSecados" label="Cant. Secados">
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>

            <Form.Item
              name="precioLavado"
              label="Precio Lavado ($)"
              tooltip="Precio por cada lavado individual. Opcional."
            >
              <PrecioIvaInput />
            </Form.Item>
            <Form.Item
              name="precioSecado"
              label="Precio Secado ($)"
              tooltip="Precio por cada secado individual. Opcional."
            >
              <PrecioIvaInput />
            </Form.Item>
          </div>

          <Divider style={{ margin: "12px 0" }} />

          {/* ── ➕ Extras ──────────────────────────────────────────────── */}
          <SectionHeader
            icon="➕"
            title="Extras (opcional)"
            hint="Servicio express, recargo por mascotas, transporte, etc. Suma al total automáticamente."
          />
          <div className="flex-container">
            <Form.Item
              className="full-width"
              name="extraDetalle"
              label="Detalle del extra"
            >
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="extraMonto" label="Monto extra">
              <PrecioIvaInput />
            </Form.Item>
          </div>
        </>
      )}

      <Divider style={{ margin: "12px 0" }} />

      {/* ── 💰 Total ──────────────────────────────────────────────── */}
      <SectionHeader
        icon="💰"
        title="Total a pagar — obligatorio"
        hint={
          costoRealMode
            ? "Monto efectivamente pagado al proveedor externo. Edita Neto o Con IVA — el otro se completa automáticamente."
            : "Edita Neto o Con IVA — el otro se completa automáticamente. Si llenaste detalles arriba, este campo ya viene calculado."
        }
      />
      <div
        style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
        }}
      >
        <Form.Item
          name="monto"
          label={
            <span style={{ fontWeight: 600 }}>
              Total Con IVA <span style={{ color: "#dc2626" }}>*</span>
            </span>
          }
          rules={[
            {
              validator: (_, value) =>
                Number(value) > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Ingresa el total (neto o con IVA)")),
            },
          ]}
          style={{ marginBottom: 8 }}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="0"
            onChange={(v) => setMontoConIva(Number(v) || 0)}
          />
        </Form.Item>
        {/*
          Input "Neto" derivado: no es un Form.Item separado para no
          duplicar fuente de verdad — se calcula desde `monto` y al
          editarlo seteamos `monto` con la conversión inversa.
        */}
        <div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>
            Total Neto (auto-calculado)
          </div>
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="0"
            value={montoNeto || null}
            onChange={(v) => setMontoFromNeto(Number(v) || 0)}
          />
        </div>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* ── 🔄 Pagos ──────────────────────────────────────────────── */}
      <SectionHeader
        icon="🔄"
        title="Pagos"
        hint={
          costoRealMode
            ? "Estructura financiera del costo real: quién pagó y a quién. La lavandería seleccionada SIEMPRE es la receptora (campo bloqueado). Puedes variar Pagado por (rol) y el Rol AF (paga / intermediario) si la realidad es distinta."
            : "Estructura financiera del lavado manual. La lavandería seleccionada en /lavanderia SIEMPRE es la receptora (campos 'Pagado a (rol)' y 'Lavandería' bloqueados). Solo puedes variar quién paga: por defecto Aloja Fácil con rol 'Paga'."
        }
      />
      <div className="flex-container">
        {/*
         * Rol de Aloja Fácil — al elegir paga/recibe, fijamos el otro
         * lado a "administrador" para que el flujo financiero (ver
         * docs/flujo-financiero.md) quede consistente.
         */}
        {/*
         * Round 2026-05-12 — opciones reducidas en lavandería:
         *   • "Recibe" eliminado del alta manual: el estimado (rol='recibe')
         *     es AUTO del booking, no se crea a mano. (Se preserva si aparece
         *     al ver/editar un registro histórico — ver rolOptions arriba.)
         *   • Cuando rolAF=intermediario, AF tampoco puede ser quien paga
         *     (ver disable de "administrador" en el Select de "Pagado por"
         *     más abajo) — por algo es intermediario.
         * Round 2026-07-21: las opciones dependen de es_generica (rolOptions):
         *   • Genérica externa → "Intermediario (neto 0)" + "Paga (costo real)".
         *   • AF interna → "Intermediario" (el backend rechaza 'paga').
         */}
        <Form.Item name="rolDeAlojaFacil" label="Rol de Aloja Fácil">
          <Select
            allowClear
            placeholder="Seleccionar rol"
            options={rolOptions}
            onChange={(val) => {
              if (val === "paga") {
                form.setFieldsValue({
                  pagadoPorRol: "administrador",
                  pagadoPorUsuarioId: null,
                });
              } else if (val === "intermediario") {
                // Si AF era el pagador, hay que limpiarlo: en intermediario
                // AF no puede pagar (ni recibir, pero el receptor ya está
                // locked a la lavandería). Sin esta limpieza la UI dejaría
                // visible "Aloja Fácil" en pagadoPorRol pese a estar
                // disabled en el dropdown.
                if (form.getFieldValue("pagadoPorRol") === "administrador") {
                  form.setFieldsValue({
                    pagadoPorRol: undefined,
                    pagadoPorUsuarioId: null,
                  });
                }
              }
            }}
          />
        </Form.Item>

        {/* Bloque "Pagado por" */}
        <Form.Item noStyle shouldUpdate>
          {() => {
            const tienePropiedad = form.getFieldValue("propiedadId") != null;
            const rolAF = form.getFieldValue("rolDeAlojaFacil");
            // Round 2026-05-08: incluir `readOnly` en el disabled.
            // ANTD da prioridad al `disabled` local sobre el del
            // <Form disabled>: si pasamos `disabled={false}` al Select
            // (que es lo que pasa cuando rolAF="intermediario"),
            // el Form contexto NO puede deshabilitarlo. Por eso
            // ORamos con readOnly para forzar el bloqueo en modo Ver.
            const bloqueado = readOnly || rolAF === "paga";
            const otroLado = form.getFieldValue("pagadoARol");
            const rolesSimples = [
              "administrador",
              "propietario",
              "arrendatario",
            ];
            // Round 2026-05-06: en modo "intermediario" NO restringimos
            // ninguna combinacion — el usuario explicito que toda combinacion
            // de pagador/receptor debe estar disponible (incluso administrador
            // con administrador, sin propiedad seleccionada, etc).
            const sinRestricciones = rolAF === "intermediario";
            return (
              <Form.Item
                name="pagadoPorRol"
                label="Pagado por (rol)"
                extra={
                  esVinculado
                    ? "Lavado de arriendo: 'Propietario' no está disponible — su costo ya se le descuenta al cliente en la limpieza, cobrárselo sería doble cobro. Solo los lavados aledaños (sueltos) se le cobran al cliente."
                    : undefined
                }
                tooltip="Quién paga el lavado: AF Empresa (lo típico), el propietario/cliente, el arrendatario, un aseador o un trabajador. La lavandería receptora no puede pagarse a sí misma. Regla: solo puedes cobrarle al CLIENTE (propietario) un lavado ALEDAÑO (suelto, sin arriendo) que él pidió; un lavado de arriendo NO se le recobra (su costo ya va en la limpieza descontada → doble cobro; el backend lo rechaza). Ver flujo-financiero.md §7.6."
              >
                <Select
                  options={ROLES.map((r) => ({
                    ...r,
                    disabled:
                      // Round 2026-05-12 iter 38: la lavandería receptora
                      // del lavado NO puede ser ella misma quien paga
                      // (sería pago a sí misma). El destino siempre es la
                      // lavandería filtrada, así que la deshabilitamos
                      // como pagador.
                      r.value === "lavanderia_catalogo" ||
                      // Round 2026-06-22 — guarda anti-doble-cobro: si el lavado
                      // está VINCULADO a un arriendo, "Propietario" (cliente) no
                      // puede pagar (su costo ya va en la limpieza descontada →
                      // doble cobro; el backend lo rechaza). flujo-financiero §7.6.
                      (esVinculado && r.value === "propietario") ||
                      // Round 2026-05-12 — cuando rolAF=intermediario, AF
                      // (administrador) NO puede ser quien paga; por
                      // definición de "intermediario" AF está en el medio,
                      // no en ninguna punta del flujo.
                      (rolAF === "intermediario" && r.value === "administrador") ||
                      (sinRestricciones
                        ? false
                        : (r.value === "propietario" && !tienePropiedad) ||
                          (rolesSimples.includes(r.value) &&
                            r.value === otroLado)),
                  }))}
                  placeholder="Selecciona rol"
                  onChange={() =>
                    form.setFieldValue("pagadoPorUsuarioId", null)
                  }
                  allowClear
                  disabled={bloqueado}
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const rol = form.getFieldValue("pagadoPorRol");
            if (
              !rol ||
              rol === "administrador" ||
              rol === "arrendatario" ||
              rol === "propietario"
            )
              return null;

            if (rol === "lavanderia_catalogo") {
              // Round 2026-05-06 (UX cleanup): la lavandería siempre es
              // editable, sin importar el modo. El dropdown de arriba es
              // solo filtro de tabla; el destino del record lo decide
              // este campo (el backend enruta según pagado_a/por.tipo=5).
              // Si el usuario está filtrando por una AF específica, la
              // pre-llenamos como default cómodo — pero sin lock.
              if (
                modo !== "general" &&
                form.getFieldValue("pagadoPorUsuarioId") == null
              ) {
                form.setFieldValue("pagadoPorUsuarioId", modo);
              }
              // Round 2026-05-12 iter 34: agrupamos visualmente las dos
              // categorías del catálogo. Mismo tipo=5 por debajo, pero el
              // admin VE la diferencia. "AF internas" = es_generica=false
              // y activas; "General Externa" = es_generica=true (única
              // entrada del sistema, no se filtra por activo porque es
              // estructural).
              const activas = lavanderias.filter((l) => l.activo);
              const internas = activas.filter((l) => !l.es_generica);
              const externa = activas.filter((l) => l.es_generica);
              const optGroups = [
                ...(internas.length
                  ? [{
                      label: "Lavanderías AF (internas)",
                      options: internas.map((l) => ({ value: l.id, label: l.nombre })),
                    }]
                  : []),
                ...(externa.length
                  ? [{
                      label: "Lavandería General Externa (proveedores externos)",
                      options: externa.map((l) => ({ value: l.id, label: l.nombre })),
                    }]
                  : []),
              ];
              return (
                <Form.Item name="pagadoPorUsuarioId" label="Lavandería">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Seleccionar lavandería"
                    options={optGroups}
                    allowClear
                  />
                </Form.Item>
              );
            }

            const list = usuariosPorRol.get(rol) || [];
            return (
              <Form.Item
                name="pagadoPorUsuarioId"
                label="Pagado por (usuario)"
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={`Usuarios rol: ${rol}`}
                  // Round 2026-05-09: label "Nombre — Propiedad" via helper
                  // compartido. Para clientes/propietarios añade el sufijo
                  // de propiedad(es); para "trabajador" lo omite.
                  options={list.map(userSelectOption)}
                  allowClear
                  dropdownRender={(menu) => {
                    if (rol !== "trabajador") return menu;
                    return (
                      <>
                        {menu}
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            padding: 8,
                            borderTop: "1px solid #f0f0f0",
                          }}
                        >
                          <Input
                            placeholder="Nuevo trabajador..."
                            id="new-usuario-pagadoPor"
                            onPressEnter={async (e) => {
                              const input = e.currentTarget;
                              const nombre = input.value.trim();
                              if (!nombre) return;
                              const nuevo = await crearOtro(nombre);
                              form.setFieldValue(
                                "pagadoPorUsuarioId",
                                nuevo.id,
                              );
                              input.value = "";
                              getMsg()?.success("Usuario creado");
                            }}
                          />
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={async () => {
                              const input = document.getElementById(
                                "new-usuario-pagadoPor",
                              ) as HTMLInputElement;
                              const nombre = input?.value.trim();
                              if (!nombre) return;
                              const nuevo = await crearOtro(nombre);
                              form.setFieldValue(
                                "pagadoPorUsuarioId",
                                nuevo.id,
                              );
                              if (input) input.value = "";
                              getMsg()?.success("Usuario creado");
                            }}
                          >
                            Crear
                          </Button>
                        </div>
                      </>
                    );
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        {/* Bloque "Pagado a" — destino del lavado.
            Round 2026-05-12 iter 38: el "Pagado a (rol)" está SIEMPRE
            locked en "Lavandería" en los lavados manuales de /lavanderia.
            La lavandería seleccionada en el filtro es la receptora del
            pago — esa es la regla universal de la página (independiente
            de costoRealMode o del Rol AF). Para lavados AUTO (booking_id
            != null) abiertos en Editar, también queda locked en el rol
            que persistió la cascada (puede ser "Aloja Fácil" para la
            genérica AUTO con pagado_a=AF tipo=0). */}
        <Form.Item noStyle shouldUpdate>
          {() => {
            return (
              <Form.Item
                name="pagadoARol"
                label="Pagado a (rol)"
                tooltip="En /lavanderia, el destino del lavado siempre es la lavandería seleccionada en el filtro de arriba. Este campo está bloqueado a propósito — para cambiar la lavandería destino, cambia el filtro primero."
              >
                <Select
                  options={ROLES.map((r) => ({
                    ...r,
                    // Round 2026-05-12 iter 38: defensivo. Aunque el
                    // Select esté disabled, marcamos "Aloja Fácil"
                    // (administrador) como opción no seleccionable
                    // dentro de los lavados manuales de /lavanderia:
                    // AF nunca recibe el pago de un lavado en este
                    // contexto (la lavandería sí). Esto NO afecta a
                    // los lavados AUTO ya guardados con pagado_a=AF;
                    // solo previene selecciones nuevas si en el futuro
                    // se quita el disabled del Select.
                    disabled: r.value === "administrador",
                  }))}
                  placeholder="Selecciona rol"
                  onChange={() => form.setFieldValue("pagadoAUsuarioId", null)}
                  allowClear={false}
                  disabled
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const rol = form.getFieldValue("pagadoARol");
            if (
              !rol ||
              rol === "administrador" ||
              rol === "arrendatario" ||
              rol === "propietario"
            )
              return null;

            if (rol === "lavanderia_catalogo") {
              // Round 2026-05-06 (UX cleanup): ver comentario simétrico en
              // el bloque "Pagado por". Siempre editable; pre-fill como
              // default si modo es AF y el campo está vacío.
              if (
                modo !== "general" &&
                form.getFieldValue("pagadoAUsuarioId") == null
              ) {
                form.setFieldValue("pagadoAUsuarioId", modo);
              }
              // Round 2026-05-12 iter 34: agrupamos AF internas vs Genérica
              // Externa (ver comentario simétrico en "Pagado por"). Además,
              // cuando estamos en sub-tab "Costo real" de la Genérica
              // (modo es la genérica + rol AF=paga), el destino DEBE ser
              // la genérica — la bloqueamos para evitar que el admin
              // accidentalmente apunte a una AF interna que rompería el
              // sentido de la entrada.
              const activas = lavanderias.filter((l) => l.activo);
              const internas = activas.filter((l) => !l.es_generica);
              const externa = activas.filter((l) => l.es_generica);
              const optGroups = [
                ...(internas.length
                  ? [{
                      label: "Lavanderías AF (internas)",
                      options: internas.map((l) => ({ value: l.id, label: l.nombre })),
                    }]
                  : []),
                ...(externa.length
                  ? [{
                      label: "Lavandería General Externa (proveedores externos)",
                      options: externa.map((l) => ({ value: l.id, label: l.nombre })),
                    }]
                  : []),
              ];
              // Round 2026-05-12 iter 38: el Select de "Lavandería"
              // destino está SIEMPRE locked en los lavados manuales de
              // /lavanderia. La lavandería receptora del pago se decide
              // únicamente por el filtro de arriba — el modal solo la
              // muestra para que el admin sepa contra qué lavandería
              // está registrando. Para cambiar el destino, cambiar el
              // filtro y reabrir.
              return (
                <Form.Item
                  name="pagadoAUsuarioId"
                  label="Lavandería"
                  tooltip="Lavandería que recibe el pago. Fijada por el filtro de arriba: para cambiar de lavandería destino, primero cambia el filtro en /lavanderia y vuelve a crear el registro."
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Seleccionar lavandería"
                    options={optGroups}
                    allowClear={false}
                    disabled
                  />
                </Form.Item>
              );
            }

            const list = usuariosPorRol.get(rol) || [];
            return (
              <Form.Item
                name="pagadoAUsuarioId"
                label="Pagado a (usuario)"
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={`Usuarios rol: ${rol}`}
                  // Round 2026-05-09: ver comentario homólogo en
                  // "Pagado por (usuario)" arriba.
                  options={list.map(userSelectOption)}
                  allowClear
                  dropdownRender={(menu) => {
                    if (rol !== "trabajador") return menu;
                    return (
                      <>
                        {menu}
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            padding: 8,
                            borderTop: "1px solid #f0f0f0",
                          }}
                        >
                          <Input
                            placeholder="Nuevo trabajador..."
                            id="new-usuario-pagadoA"
                            onPressEnter={async (e) => {
                              const input = e.currentTarget;
                              const nombre = input.value.trim();
                              if (!nombre) return;
                              const nuevo = await crearOtro(nombre);
                              form.setFieldValue(
                                "pagadoAUsuarioId",
                                nuevo.id,
                              );
                              input.value = "";
                              getMsg()?.success("Usuario creado");
                            }}
                          />
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={async () => {
                              const input = document.getElementById(
                                "new-usuario-pagadoA",
                              ) as HTMLInputElement;
                              const nombre = input?.value.trim();
                              if (!nombre) return;
                              const nuevo = await crearOtro(nombre);
                              form.setFieldValue(
                                "pagadoAUsuarioId",
                                nuevo.id,
                              );
                              if (input) input.value = "";
                              getMsg()?.success("Usuario creado");
                            }}
                          >
                            Crear
                          </Button>
                        </div>
                      </>
                    );
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>
    </Form>
  );
};

export default LavadoForm;
